<template>
  <!-- Vysual Only: flat nav items instead of dropdown -->
  <template v-if="clientIsVysualOnly">
    <router-link
      class="navbar-item client-nav-link"
      :class="{ 'is-active': isActiveRoute('account-assets') }"
      :to="{
        name: 'account-assets',
        params: { id: currentRouteCompanyId },
      }"
    >
      <span
        class="icon"
        :class="{ 'has-text-primary': isActiveRoute('account-assets') }"
      >
        <font-awesome-icon
          :icon="['fal', 'photo-film']"
          :active-icon="['fadl', 'photo-film']"
          :active="isActiveRoute('account-assets')"
        />
      </span>
      <span :class="{ 'has-text-primary': isActiveRoute('account-assets') }">Library</span>
    </router-link>

    <router-link
      v-if="clientCanAccessSharing"
      class="navbar-item client-nav-link"
      :class="{ 'is-active': isActiveRoute('account-sharing') }"
      :to="{
        name: 'account-sharing',
        params: { id: currentRouteCompanyId },
      }"
    >
      <span
        class="icon"
        :class="{ 'has-text-primary': isActiveRoute('account-sharing') }"
      >
        <font-awesome-icon
          :icon="['fal', 'share-nodes']"
          :active-icon="['fadl', 'share-nodes']"
          :active="isActiveRoute('account-sharing')"
        />
      </span>
      <span :class="{ 'has-text-primary': isActiveRoute('account-sharing') }">Sharing</span>
    </router-link>

    <router-link
      v-if="clientCanAccessUpload"
      class="navbar-item client-nav-link"
      :class="{ 'is-active': isActiveRoute('account-upload') }"
      :to="{
        name: 'account-upload',
        params: { id: currentRouteCompanyId },
      }"
    >
      <span
        class="icon"
        :class="{ 'has-text-primary': isActiveRoute('account-upload') }"
      >
        <font-awesome-icon
          :icon="['fal', 'cloud-upload']"
          :active-icon="['fadl', 'cloud-upload']"
          :active="isActiveRoute('account-upload')"
        />
      </span>
      <span :class="{ 'has-text-primary': isActiveRoute('account-upload') }">Upload</span>
    </router-link>
  </template>

  <!-- Standard: Assets Dropdown -->
  <div
    v-else
    class="navbar-item has-dropdown is-hoverable settings-dropdown client-nav-link"
    :class="{ 'is-active': isAssetsActive }"
  >
    <a class="navbar-link settings-link">
      <span class="icon">
        <font-awesome-icon
          :icon="['fal', 'photo-film']"
          :active-icon="['fadl', 'photo-film']"
          :active="isAssetsActive"
        />
      </span>
      <span>Assets</span>
    </a>
    <div class="navbar-dropdown">
      <router-link
        :to="{
          name: 'account-assets',
          params: { id: currentRouteCompanyId },
        }"
        class="navbar-item"
        :class="{ 'is-active': isActiveRoute('account-assets') }"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'photo-film']" />
        </span>
        <span>Asset Library</span>
      </router-link>
      <router-link
        v-if="clientCanAccessUpload"
        :to="{
          name: 'account-upload',
          params: { id: currentRouteCompanyId },
        }"
        class="navbar-item"
        :class="{ 'is-active': isActiveRoute('account-upload') }"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'cloud-upload']" />
        </span>
        <span>Upload</span>
      </router-link>
      <router-link
        v-if="clientCanAccessSharing"
        :to="{
          name: 'account-sharing',
          params: { id: currentRouteCompanyId },
        }"
        class="navbar-item"
        :class="{ 'is-active': isActiveRoute('account-sharing') }"
      >
        <span class="icon is-small">
          <font-awesome-icon :icon="['fal', 'share-nodes']" />
        </span>
        <span>Sharing</span>
      </router-link>
    </div>
  </div>

  <router-link
    v-if="clientCanAccessProjects"
    class="navbar-item client-nav-link"
    :class="{ 'is-active': isActiveRoute('account-projects') }"
    :to="{
      name: 'account-projects',
      params: {
        id: currentRouteCompanyId,
      },
    }"
  >
    <span
      class="icon"
      :class="{
        'has-text-primary': isActiveRoute('account-projects'),
      }"
    >
      <font-awesome-icon
        :icon="['fal', 'rectangle-list']"
        :active-icon="['fadl', 'rectangle-list']"
        :active="isActiveRoute('account-projects')"
      />
    </span>
    <span
      :class="{
        'has-text-primary': isActiveRoute('account-projects'),
      }"
      >Projects</span
    >
  </router-link>

  <router-link
    v-if="clientCanAccessShows"
    class="navbar-item client-nav-link"
    :class="{ 'is-active': isActiveRoute('account-shows') || isActiveRoute('account-show-detail') }"
    :to="{
      name: 'account-shows',
      params: {
        id: currentRouteCompanyId,
      },
    }"
  >
    <span
      class="icon podcast-icon-wrapper"
      :class="{
        'has-text-primary': isActiveRoute('account-shows') || isActiveRoute('account-show-detail'),
      }"
    >
      <font-awesome-icon
        :icon="['fal', 'podcast']"
        :active-icon="['fadl', 'podcast']"
        :active="isActiveRoute('account-shows') || isActiveRoute('account-show-detail')"
      />
    </span>
    <span
      :class="{
        'has-text-primary': isActiveRoute('account-shows') || isActiveRoute('account-show-detail'),
      }"
      >Podcasts</span
    >
  </router-link>

  <router-link
    v-if="clientCanAccessCalendar"
    class="navbar-item client-nav-link position-relative"
    :class="{ 'is-active': isActiveRoute('account-calendar') }"
    :to="{
      name: 'account-calendar',
      params: {
        id: currentRouteCompanyId,
      },
    }"
    :title="
      hasProjectsNeedingDates ? 'Select dates for your projects' : ''
    "
  >
    <span
      class="icon calendar-icon-wrapper"
      :class="{
        'has-text-primary': isActiveRoute('account-calendar'),
      }"
    >
      <font-awesome-icon
        :icon="['fal', 'calendar']"
        :active-icon="['fadl', 'calendar']"
        :active="isActiveRoute('account-calendar')"
      />
      <!-- Red dot notification -->
      <span
        v-if="hasProjectsNeedingDates"
        class="notification-dot"
      ></span>
    </span>
    <span
      :class="{
        'has-text-primary': isActiveRoute('account-calendar'),
      }"
      >Calendar</span
    >
  </router-link>

  <router-link
    v-if="clientCanAccessReviews"
    class="navbar-item client-nav-link position-relative"
    :class="{ 'is-active': isActiveRoute('account-reviews') }"
    :to="{
      name: 'account-reviews',
      params: {
        id: currentRouteCompanyId,
      },
    }"
    :title="hasActiveReviews ? 'You have assets awaiting review' : ''"
  >
    <span
      class="icon review-icon-wrapper"
      :class="{
        'has-text-primary': isActiveRoute('account-reviews'),
      }"
    >
      <font-awesome-icon
        :icon="['fal', 'list-check']"
        :active-icon="['fadl', 'list-check']"
        :active="isActiveRoute('account-reviews')"
      />
      <span
        v-if="hasActiveReviews"
        class="notification-dot"
      ></span>
    </span>
    <span
      :class="{
        'has-text-primary': isActiveRoute('account-reviews'),
      }"
      >Reviews</span
    >
  </router-link>
</template>

<script setup>
import { useNavbarState } from '@/composables/useNavbarState'
import { useClientPermissions } from '@/composables/useClientPermissions'


const {
  currentRouteCompanyId,
  isActiveRoute,
  isAssetsActive,
  hasProjectsNeedingDates,
  hasActiveReviews,
} = useNavbarState()

const {
  isVysualOnly: clientIsVysualOnly,
  canAccessUpload: clientCanAccessUpload,
  canAccessSharing: clientCanAccessSharing,
  canAccessProjects: clientCanAccessProjects,
  canAccessCalendar: clientCanAccessCalendar,
  canAccessShows: clientCanAccessShows,
  canAccessReviews: clientCanAccessReviews,
} = useClientPermissions()
</script>

<style lang="scss" scoped>
@import '@/assets/scss/navbar-shared';

// Client section links only: smaller icons, tighter icon-label gap.
// Doubled class beats navBar.vue's `.navbar .navbar-item { gap }` and the
// shared `.settings-link { gap !important }` without touching Nexus/agency nav.
.client-nav-link.client-nav-link {
  gap: 0.25rem;

  &.settings-dropdown .settings-link {
    gap: 0.25rem !important;
  }

  > .icon,
  > .settings-link > .icon {
    width: 1.25rem;
    font-size: 0.85rem;
  }
}
</style>
